import { memo, Suspense, useCallback } from "react"
import { useFetchLocation } from "@/common/queries/use-fetch-location"
import { useBeUserData } from "@/common/queries/use-user"

import type { BannerData, HomeService, HomeSession } from "@be-miniapp/api"
import { useTranslation } from "@be-miniapp/i18n"
import { lazily } from "@be-miniapp/shared/utils/lazily"
import { SpinnerScreen } from "@be-miniapp/ui/components/ui/spinner"
import { useNavigate } from "@be-miniapp/ui/hooks/use-navigate"

import { useHomeActions } from "@/hooks/use-home-actions"
import { useOptimizedHomeData } from "@/hooks/use-optimized-home-data"
import { useTripRating } from "@/hooks/use-trip-rating"
import { useClientSetting } from "@/store/client-setting"

import { ActiveSessions } from "./active-sessions"
import { HomeTierBackground } from "./components/home-background"
import { HomeDiscoverySession } from "./home-discovery-session"
import { HomeIconScrollList } from "./icon-scroll-list"
import { RecommendedAddressHomeScreen } from "./recommended-address"

const { HomeCarousel } = lazily(() => import("./carousel"))

const MainTabContent = memo(
  ({
    lang,
    welcomeTitle,
    isLoading,
    services,
    banners,
    discoveryData,
    onServiceClick,
    onOpenDropoff,
    onSlideClick,
    homeContent,
    dropoffLabel,
  }: {
    lang: string
    welcomeTitle: string
    isLoading: boolean
    services: HomeService[]
    banners: BannerData[]
    discoveryData: HomeSession | null
    onServiceClick: (service: HomeService) => void
    onOpenDropoff: () => void
    onSlideClick: (slide: BannerData) => void
    homeContent?: string
    dropoffLabel: string
  }) => {
    const headerCard = (
      <div className="relative isolate rounded-3xl bg-be-zalo-home-bg pt-4 shadow-[0_10px_30px_rgba(0,0,29,0.08)]">
        <h1 className="truncate px-4 font-semibold text-base text-be-text leading-5">
          {welcomeTitle}
        </h1>

        <div className="mt-3 overflow-hidden rounded-[24px] bg-white shadow-[0_6px_22px_rgba(0,0,29,0.08)]">
          <button
            type="button"
            className="flex min-h-[62px] w-full items-center gap-2 px-3 text-left"
            onClick={onOpenDropoff}
          >
            <img
              src="/icons/destination.svg"
              alt=""
              className="size-7 shrink-0"
            />
            <span className="min-w-0 flex-1 truncate text-be-text-sub leading-8">
              {dropoffLabel}
            </span>
          </button>
          <RecommendedAddressHomeScreen />
          <ActiveSessions showDropoffInput={false} />
        </div>
      </div>
    )

    return (
      <div className="relative min-h-full bg-white pb-24">
        {homeContent ? (
          <HomeTierBackground
            className="min-h-[313px]"
            contentClassName="px-4 pt-[100px] pb-12"
            homeContent={homeContent}
          >
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-[rgba(255,255,255,0)] via-[rgba(255,255,255,0.55)] to-white" />
            {headerCard}
          </HomeTierBackground>
        ) : (
          <div className="relative min-h-[313px] overflow-hidden bg-be-zalo-home-yellow px-4 pt-[100px] pb-12">
            <div className="absolute top-[-88px] left-[-18%] h-[196px] w-[78%] rotate-[32deg] bg-be-zalo-home-cream/62" />
            <div className="absolute top-[-92px] right-[-26%] h-[228px] w-[92%] rotate-[-52deg] bg-be-zalo-home-cream/58" />
            <div className="absolute right-[-32%] bottom-[-30px] h-[128px] w-[128%] rotate-[12deg] bg-be-zalo-home-band/78" />
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-[rgba(255,255,255,0)] via-[rgba(255,255,255,0.55)] to-white" />
            {headerCard}
          </div>
        )}

        <div className="relative z-30 flex max-w-full flex-col gap-5 bg-white pt-0">
          <HomeIconScrollList
            listOfServices={services}
            lang={lang}
            isLoading={isLoading}
            onServiceClick={onServiceClick}
          />

          <Suspense>
            <HomeCarousel
              isLoading={isLoading}
              onClick={onSlideClick}
              listOfSlides={banners}
            />
          </Suspense>

          {discoveryData ? (
            <HomeDiscoverySession sessionMetadata={discoveryData} />
          ) : null}
        </div>
      </div>
    )
  }
)

MainTabContent.displayName = "MainTabContent"

const MainTab2 = memo(() => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const lang = useClientSetting((state) => state.language)
  const { fetch: fetchLocationBeforeRide, isFetching: isFetchingLocation } =
    useFetchLocation()
  const userName =
    (useBeUserData((state) => state.data?.user_name) as
      | string
      | null
      | undefined) ?? ""

  const { banners, services, homeBackground, discoveryData, isLoading } =
    useOptimizedHomeData()

  const { handleServiceClick, handleSlideClick } = useHomeActions(
    lang,
    fetchLocationBeforeRide
  )

  useTripRating({
    enabled: !!services.length,
  })

  const handleOpenDropoff = useCallback(async () => {
    await fetchLocationBeforeRide()
    navigate("/ride/dropoff_location", {
      state: {
        resetLocation: true,
      },
    })
  }, [fetchLocationBeforeRide, navigate])

  return (
    <div className="no-scrollbar flex size-full min-h-0 w-full flex-col overflow-y-auto overscroll-none bg-white">
      {isFetchingLocation && <SpinnerScreen />}

      <MainTabContent
        lang={lang}
        welcomeTitle={t("home.welcome", { name: userName || "bạn" })}
        isLoading={isLoading}
        services={services}
        banners={banners}
        discoveryData={discoveryData ?? null}
        onServiceClick={handleServiceClick}
        onOpenDropoff={handleOpenDropoff}
        onSlideClick={handleSlideClick}
        homeContent={homeBackground.home_content}
        dropoffLabel={t("home.input_dropoff")}
      />
    </div>
  )
})

MainTab2.displayName = "MainTab2"

export { MainTab2 }
